import React, { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon, ClockIcon, UsersIcon } from 'lucide-react';
import { taskBriefs, TaskBrief } from '../data/briefs';
import { useTypewriter } from '../hooks/useTypewriter';
import { Reveal } from './Reveal';
type View = 'brief' | 'api';
const ease = [0.23, 1, 0.32, 1] as const;
export function TaskBriefs() {
  const [activeId, setActiveId] = useState(taskBriefs[0].id);
  const [view, setView] = useState<View>('brief');
  const active = taskBriefs.find(b => b.id === activeId) ?? taskBriefs[0];
  return <section id="briefs" className="mx-auto max-w-6xl scroll-mt-20 px-6 py-28" data-id="element-256" data-mp="1qv9ajsqoem4t">
      <Reveal className="max-w-2xl" data-id="element-257" data-mp="1lvmuzcndktsu">
        <h2 className="font-display text-4xl font-semibold tracking-tight md:text-5xl" data-id="element-258" data-mp="1lwo7zmnybqd7">If it happens IRL, it's a task.</h2>
        <p className="mt-4 text-lg text-muted" data-id="element-259" data-mp="1gy4i5b68lufk">Real briefs companies push to the network every day. Click one and see exactly what lands on a tasker's phone.</p>
      </Reveal>

      <div className="mt-14 grid gap-8 lg:grid-cols-[260px_1fr]" data-id="element-260" data-mp="3isko7bongsgk">
        <div role="tablist" aria-label="Task categories" aria-orientation="vertical" className="flex gap-2 overflow-x-auto lg:flex-col lg:gap-0" data-id="element-261" data-mp="3nl3z20vyd9ho">
          {taskBriefs.map(brief => {
          const selected = brief.id === activeId;
          return <button key={brief.id} role="tab" aria-selected={selected} aria-controls="brief-panel" onClick={() => setActiveId(brief.id)} className={`relative shrink-0 rounded-xl px-4 py-3 text-left transition-colors duration-150 lg:rounded-none lg:border-l-2 lg:border-line lg:py-4 ${selected ? 'bg-surface lg:bg-transparent' : 'text-muted hover:text-ink'}`} data-id="element-262" data-mp="3dsptex1r7iq8">
                {selected && <motion.span layoutId="brief-rail" className="absolute -left-[2px] top-0 hidden h-full w-[2px] bg-accent lg:block" transition={{
              duration: 0.25,
              ease
            }} data-id="element-263" data-mp="2l7g8k463yshh" />}
                <span className={`block whitespace-nowrap font-medium ${selected ? 'text-ink' : ''}`} data-id="element-264" data-mp="mobenma3epdy">{brief.category}</span>
                <span className="mt-0.5 hidden text-sm text-muted lg:block" data-id="element-265" data-mp="hwjwo0505rzf">{brief.summary}</span>
              </button>;
        })}
        </div>

        <div id="brief-panel" role="tabpanel" className="overflow-hidden rounded-3xl border border-line bg-surface" data-id="element-266" data-mp="7zfcg5upw6l9">
          <div className="flex items-center justify-between border-b border-line px-5 py-3" data-id="element-267" data-mp="3xtz6je9tqaj">
            <span className="font-mono text-xs text-muted" data-id="element-268" data-mp="3v3ld8kerxcgz">
              <span className="text-accent" data-id="element-269" data-mp="3v67vpf37dcdz">POST</span> /v1/tasks
            </span>
            <div className="inline-flex rounded-lg bg-canvas p-0.5 font-mono text-xs" role="group" aria-label="View" data-id="element-270" data-mp="3q74obnzr2u2u">
              {(['brief', 'api'] as View[]).map(v => <button key={v} onClick={() => setView(v)} aria-pressed={view === v} className={`relative rounded-md px-3 py-1 transition-colors duration-150 ${view === v ? 'text-ink' : 'text-muted hover:text-ink'}`} data-id="element-271" data-mp="1of8eqjx57uap">
                  {view === v && <motion.span layoutId="brief-view" className="absolute inset-0 rounded-md bg-surface shadow-sm" transition={{
                duration: 0.2,
                ease
              }} data-id="element-272" data-mp="25pccbn2gh2nu" />}
                  <span className="relative" data-id="element-273" data-mp="1czdjdgl54ldw">{v === 'brief' ? 'brief' : 'json'}</span>
                </button>)}
            </div>
          </div>

          <div className="min-h-[220px] border-b border-line p-6 md:p-10" data-id="element-274" data-mp="1hzhzh5i4ze3l">
            <AnimatePresence mode="wait" data-id="element-275" data-mp="1mvgi0cgsa1w3">
              <motion.div key={`${active.id}-${view}`} initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -6
            }} transition={{
              duration: 0.16,
              ease
            }} data-id="element-276" data-mp="1rvl3kgwjtmt6">
                {view === 'brief' ? <BriefText brief={active} data-id="element-277" data-mp="30w396zn71da2" /> : <JsonView brief={active} data-id="element-278" data-mp="30xec6x8teh04" />}
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="grid gap-6 p-6 sm:grid-cols-[1fr_1fr_1fr_auto] sm:items-center md:px-10" data-id="element-279" data-mp="2wwc9u7jlpotx">
            <Metric label="Tasker earns" value={active.payout} large data-id="element-280" data-mp="3aw3s4kh5hrdq" />
            <Metric label="Median completion" value={active.medianTime} icon={<ClockIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-282" data-mp="3jv7b7ogbi6s8" />} data-id="element-281" data-mp="35v6965obgtb6" />
            <Metric label="Taskers nearby" value={active.availableTaskers} icon={<UsersIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-284" data-mp="3fskjdnjnclv1" />} data-id="element-283" data-mp="3jte1g0aer6g0" />
            <a href="#start" className="group inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-ink px-5 py-2.5 text-sm font-medium text-canvas transition-transform duration-150 ease-out active:scale-[0.97]" data-id="element-285" data-mp="3tljuzegb5vxb">
              Ship this task
              <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-1" aria-hidden="true" data-id="element-286" data-mp="2fif8sleee5c" />
            </a>
          </div>
        </div>
      </div>
    </section>;
}
function BriefText({
  brief
}: {
  brief: TaskBrief;
}) {
  const reduce = useReducedMotion();
  const {
    output,
    done
  } = useTypewriter(brief.instruction, !reduce);
  return <p className="font-mono text-sm leading-7 md:text-base md:leading-8" data-id="element-287" data-mp="h0wm9qrnn5zx">
      <span className="sr-only" data-id="element-288" data-mp="h3929ydur6ac">{brief.instruction}</span>
      <span className="text-accent" data-id="element-289" data-mp="2bqe3daua5fw">&lt;location value=&quot;{brief.location}&quot; /&gt;</span>{' '}
      <span aria-hidden="true" data-id="element-290" data-mp="741oja37h9mp">{output}</span>
      {!done && <span aria-hidden="true" className="ml-0.5 inline-block h-5 w-2 translate-y-1 animate-pulse bg-accent" data-id="element-291" data-mp="3phhp0ptp12ul" />}
      {done && <motion.span initial={{
      opacity: 0,
      scale: 0.96
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      duration: 0.18
    }} className="ml-1 inline-block rounded bg-accent-soft px-1.5 text-accent" data-id="element-292" data-mp="3uhdrgkmqsm81">
          [{brief.proof}]
        </motion.span>}
    </p>;
}
function JsonView({
  brief
}: {
  brief: TaskBrief;
}) {
  const rows: [string, string][] = [['location', `"${brief.location}"`], ['category', `"${brief.id}"`], ['proof', `"${brief.proof.replace('_required', '')}"`], ['payout', `"${brief.payout}"`], ['sla', `"${brief.medianTime}"`]];
  return <pre className="overflow-x-auto font-mono text-sm leading-7" data-id="element-293" data-mp="2mpeu9s1jcuom">
      <span className="text-muted" data-id="element-294" data-mp="3kcdu2bniyiqq">{'{'}</span>
      {rows.map(([k, v], i) => <span key={k} className="block pl-5" data-id="element-295" data-mp="3kab57uy3yvnl" data-mpi={i}>
          <span className="text-ink" data-id="element-296" data-mp="3fd17zt4h9fp7">"{k}"</span>
          <span className="text-muted" data-id="element-297" data-mp="3fb7ze8ab0lek">: </span>
          <span className="text-accent" data-id="element-298" data-mp="3adevxn0dy5hz">{v}</span>
          {i < rows.length - 1 && <span className="text-muted" data-id="element-299" data-mp="35f4yt6v3outq">,</span>}
        </span>)}
      <span className="text-muted" data-id="element-300" data-mp="35ev9zmlsktpm">{'}'}</span>
    </pre>;
}
function Metric({
  label,
  value,
  icon,
  large
}: {
  label: string;
  value: string;
  icon?: React.ReactNode;
  large?: boolean;
}) {
  return <div data-id="element-301" data-mp="1tyn1z5nlcq6k">
      <p className="flex items-center gap-1.5 text-xs text-muted" data-id="element-302" data-mp="1u22ga69fxv6x">
        {icon}
        {label}
      </p>
      <AnimatePresence mode="wait" data-id="element-303" data-mp="9g2urv0m331t">
        <motion.p key={value} initial={{
        opacity: 0,
        y: 6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -6
      }} transition={{
        duration: 0.16
      }} className={large ? 'mt-1 font-display text-2xl font-semibold' : 'mt-1 text-lg font-medium'} data-id="element-304" data-mp="eg8qbma059p6">
          {value}
        </motion.p>
      </AnimatePresence>
    </div>;
}